import { ApiError } from '@/lib/api-error'

export interface ApiEnvelope {
    success: boolean
    message?: string
}

/**
 * This API answers `200 OK` with `{ success: false, message }` for domain failures --
 * a refund the ledger rejected, a duplicate already resolved by someone else. The
 * fetch client only throws on HTTP status, so without this every such failure would
 * read as a success and the UI would optimistically show the wrong state.
 *
 * The legacy app checked `response.success` at some call sites and not others, which
 * is exactly the inconsistency this removes: every response goes through here.
 */
/**
 * Narrows a discriminated response union to its success branch, so callers can read
 * payload fields that only exist on success. Response types written as a single
 * `success: boolean` interface fall through unchanged.
 */
type Succeeded<T> = [Extract<T, { success: true }>] extends [never] ? T : Extract<T, { success: true }>

export function expectSuccess<T extends ApiEnvelope>(response: T, fallback: string): Succeeded<T> {
    if (response.success) return response as Succeeded<T>
    throw new ApiError({
        status: 200,
        body: response,
        url: '',
        message: response.message ?? fallback
    })
}

/** 24-character hex. Several endpoints 400 on anything else, so filters gate on it. */
export function isObjectId(value: string): boolean {
    return /^[a-f\d]{24}$/i.test(value.trim())
}

/**
 * Pulls every page of a paginated endpoint, for exports.
 *
 * Guards on three conditions rather than one: the reported total, a short page, and
 * a hard page cap. The legacy exports trusted `count` alone, so a `count` that grew
 * mid-export (new deposits arriving) looped until the tab died.
 */
export async function fetchAllPages<TRow>(
    fetchPage: (page: number) => Promise<{ rows: TRow[]; total: number }>,
    { pageSize, maxPages = 100, onProgress }: { pageSize: number; maxPages?: number; onProgress?: (n: number) => void }
): Promise<TRow[]> {
    const all: TRow[] = []

    for (let page = 1; page <= maxPages; page += 1) {
        const { rows, total } = await fetchPage(page)
        all.push(...rows)
        onProgress?.(all.length)

        if (rows.length < pageSize) break
        if (all.length >= total) break
    }

    return all
}
